/*
 * tabGuard — Zwei-Tab-Erkennung fuer Projekte (BroadcastChannel).
 *
 * Kein Lock, nur eine deutliche Warnung: Wird dasselbe Projekt in einem
 * zweiten Tab desselben Browsers geoeffnet, warnen beide Tabs
 * (Testphasen-Entscheidung 2026-07-02). Der Zwei-Geraete-Fall bleibt
 * Last-Write-Wins im Cloud-Sync — dafuer waere ein Server-Lock noetig
 * (Muster siehe MemoryEditor).
 *
 * Protokoll: der oeffnende Tab sendet 'probe' mit der Projekt-ID; jeder
 * Tab, der dieses Projekt gerade offen hat, antwortet mit 'occupied'.
 * Der Oeffner warnt bei jeder 'occupied'-Antwort, der bestehende Tab
 * beim 'probe' nur einmal pro Projekt (sonst nervt jeder Tab-Wechsel
 * des Zweit-Tabs den Schreibfluss im Erst-Tab).
 */

const CHANNEL_NAME = 'carbisdale-tab-guard'

/**
 * @param {object} options
 * @param {() => string|null} options.getCurrentProjectId - Liefert die aktuell offene Projekt-ID
 * @param {(projectId: string) => void} options.onConflict - Wird bei erkanntem Doppel-Open gerufen
 * @returns {{ notifyProjectOpen(projectId: string): void, dispose(): void }}
 */
export function createTabGuard({ getCurrentProjectId, onConflict }) {
    // Aeltere Browser ohne BroadcastChannel: Guard wird zum No-Op
    if (typeof BroadcastChannel === 'undefined') {
        return { notifyProjectOpen() {}, dispose() {} }
    }

    const tabId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tab-${Math.random().toString(36).slice(2)}`
    const channel = new BroadcastChannel(CHANNEL_NAME)
    const warnedAsHolder = new Set()
    // Pro Probe nur eine Warnung, auch wenn mehrere Tabs 'occupied' antworten
    let openerWarnedFor = null

    channel.onmessage = (event) => {
        const msg = event && event.data ? event.data : {}
        if (!msg.projectId || msg.tabId === tabId) return
        if (msg.projectId !== getCurrentProjectId()) return

        if (msg.type === 'probe') {
            // Ein anderer Tab oeffnet unser Projekt: antworten + selbst warnen
            channel.postMessage({ type: 'occupied', projectId: msg.projectId, tabId })
            if (!warnedAsHolder.has(msg.projectId)) {
                warnedAsHolder.add(msg.projectId)
                onConflict(msg.projectId)
            }
        } else if (msg.type === 'occupied') {
            // Wir haben gerade ein Projekt geoeffnet, das woanders offen ist
            if (openerWarnedFor !== msg.projectId) {
                openerWarnedFor = msg.projectId
                onConflict(msg.projectId)
            }
        }
    }

    return {
        /** Beim Oeffnen/Wechseln eines Projekts rufen. */
        notifyProjectOpen(projectId) {
            if (!projectId) return
            openerWarnedFor = null
            channel.postMessage({ type: 'probe', projectId, tabId })
        },
        dispose() {
            channel.close()
        }
    }
}
